import * as PropTypes from 'prop-types';

import {User, UserMetadata} from 'constants/UserConstants';
import {
  Organization,
  ORGANIZATION_REQUEST,
  OrganizationPermissionRole,
  OrganizationRequest,
  Team,
} from 'orgs/OrganizationConstants';

export enum ReferralStatus {
  PENDING = 'pending',
  MEMBER = 'member',
}

interface OrgStatus {
  organizationId: string;
  status: ReferralStatus;
}

export interface Referral {
  email: string;
  user: User | null;
  createdAt: string;
  orgStatuses: Array<OrgStatus>;
}

export interface EntityStoreUser extends User {
  canMakePublic?: boolean;
  disablePublic?: boolean;
  organizationIds?: Array<string>;
  organizations: Array<Organization>;
  teamIds?: Array<string>;
  teams: Array<Team>;
  currentUserInboundRequests: Array<OrganizationRequest>;
  currentUserOutboundRequests?: Array<OrganizationRequest>;
  pendingOrgInvites?: number;
  userOrgsInboundRequests: Array<OrganizationRequest>;
  referrals?: Array<Referral>;
}

/** An organization request normalized to reference cached entities by ID. */
export interface StoredOrganizationRequest extends Omit<
  OrganizationRequest,
  'organization' | 'user' | 'inviter'
> {
  organizationId: string;
  userId: string;
  inviterId: string | null;
}

/** An organization request received from the server before EntityStore normalization. */
export interface ServerOrganizationRequest extends Omit<OrganizationRequest, 'inviter'> {
  inviter: User | null;
}

/** An organization request hydrated with entities from the EntityStore cache. */
export interface HydratedOrganizationRequest extends Omit<
  OrganizationRequest,
  'organization' | 'user' | 'inviter'
> {
  organization: Organization | undefined;
  user: User | undefined;
  inviter: User | undefined;
}

/** A referral normalized to reference a cached user by ID. */
export interface StoredReferral extends Omit<Referral, 'user'> {
  userId: string | null;
}

/** A referral hydrated with its user from the EntityStore cache. */
export interface HydratedReferral extends Omit<Referral, 'user'> {
  user: User | null | undefined;
}

/** Permission data merged into a cached user when listing group members. */
export interface GroupPermission {
  userId: string;
  initials: string;
  level?: OrganizationPermissionRole;
  role?: OrganizationPermissionRole;
}

/** Options for updating the organizations associated with a cached user. */
export interface AddOrganizationsOptions {
  append?: boolean;
}

/** Fields EntityStore adds to canonical users, organizations, and teams. */
export interface EntityStoreManagedFields {
  currentUserInboundRequests?: Array<StoredOrganizationRequest>;
  currentUserOutboundRequests?: Array<StoredOrganizationRequest>;
  disablePublic?: boolean;
  extraSpaceTasks?: unknown;
  initials?: string;
  metadata?: UserMetadata;
  organizationIds?: Array<string>;
  pendingOrgInvites?: number;
  referrals?: Array<StoredReferral>;
  teamIds?: Array<string>;
  userOrgsInboundRequests?: Array<StoredOrganizationRequest>;
}

/** A canonical party entity augmented with fields managed by EntityStore. */
export type EntityStoreEntity = (User | Organization | Team) & EntityStoreManagedFields;

export const ENTITY_STORE_USER_SHAPE = {
  avatar_url: PropTypes.string.isRequired,
  currentUserInboundRequests: PropTypes.arrayOf(ORGANIZATION_REQUEST),
  handle: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  url_identifier: PropTypes.string.isRequired,
  pendingOrgInvites: PropTypes.number,
  userOrgsInboundRequests: PropTypes.arrayOf(ORGANIZATION_REQUEST),
};
